import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, type FC, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { cn } from '../lib/utils';

// Figma "Card" (design system `457:3316`) — fully owned (not wrapped from `ui/card.tsx`,
// same as Badge.tsx): every shadcn default on the root and its slots (shadow-sm, bg-card,
// py-6, gap-6, font-semibold/leading-none title, text-sm/text-muted-foreground
// description) existed only to be immediately overridden, none of it is Figma-driven, and
// keeping the wrap-and-override indirection just means every future edit here has to
// remember to keep fighting classes it no longer needs to fight. `variant` mirrors
// Item.tsx's vocabulary (default/outline/muted) so the two container primitives read the
// same way at call sites. `elevated` is the only one Figma actually specifies; `outline`
// and `muted` exist because real consumers (SDM's EmptyLayout, Agent's
// FilterStepEditor, AlarmsDashboardView) already hand-roll them today.
const cardVariants = cva('flex flex-col gap-3 rounded-lg text-foreground-primary', {
  variants: {
    variant: {
      elevated: 'border border-transparent bg-background-surface shadow-elevation-1',
      outline: 'border border-border-muted bg-background-surface shadow-none',
      muted: 'border border-transparent bg-background-muted shadow-none',
    },
    padding: {
      md: 'p-4',
      sm: 'p-3',
      xs: 'p-2',
      none: 'p-0',
    },
  },
  defaultVariants: {
    variant: 'elevated',
    padding: 'md',
  },
});

export type CardVariant = NonNullable<VariantProps<typeof cardVariants>['variant']>;
export type CardPadding = NonNullable<VariantProps<typeof cardVariants>['padding']>;

type CardProps = ComponentPropsWithoutRef<'div'> & VariantProps<typeof cardVariants>;
type CardHeaderProps = ComponentPropsWithoutRef<'div'>;
type CardContentProps = ComponentPropsWithoutRef<'div'>;
type CardFooterProps = ComponentPropsWithoutRef<'div'>;
type CardTitleProps = ComponentPropsWithoutRef<'div'>;
type CardDescriptionProps = ComponentPropsWithoutRef<'div'>;
type CardActionProps = ComponentPropsWithoutRef<'div'>;

export const Card: FC<CardProps> = (props) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { onClick, variant, padding, className, children, ...rest } = props;
  return (
    <div
      ref={cardRef}
      {...rest}
      data-slot="card"
      className={cn(
        cardVariants({ variant, padding }),
        onClick &&
          'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        className,
      )}
      // This is necessary to handle clicks outside the card, for example when clicking on a button in a dialog or popover
      onClick={(e) => {
        if (cardRef.current && cardRef.current.contains(e.target as Node)) {
          onClick?.(e);
        }
      }}
    >
      <ErrorBoundary
        fallbackRender={() => (
          <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
            <p className="text-sm text-muted-foreground">Error loading card</p>
          </div>
        )}
      >
        {children}
      </ErrorBoundary>
    </div>
  );
};

// The container owns all padding via the `padding` variant above, so slots are pure
// layout — `has-data-[slot=card-action]:grid-cols-[1fr_auto]` + the grid-rows/items-start
// below is the one structural bit ported from shadcn: it's what lets `CardAction` sit
// beside the title/description block instead of below it (see CameraRail.tsx).
export const CardHeader: FC<CardHeaderProps> = (props) => (
  <div
    {...props}
    data-slot="card-header"
    className={cn(
      '@container/card-header has-data-[slot=card-action]:grid-cols-[1fr_auto] grid auto-rows-min grid-rows-[auto_auto] items-start gap-1',
      props.className,
    )}
  />
);
export const CardContent: FC<CardContentProps> = (props) => (
  <div {...props} data-slot="card-content" className={props.className} />
);
export const CardFooter: FC<CardFooterProps> = (props) => (
  <div {...props} data-slot="card-footer" className={cn('flex items-center', props.className)} />
);
export const CardAction: FC<CardActionProps> = (props) => (
  <div
    {...props}
    data-slot="card-action"
    className={cn(
      'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
      props.className,
    )}
  />
);

// Figma title/subtitle text styles.
export const CardTitle: FC<CardTitleProps> = (props) => (
  <div
    {...props}
    data-slot="card-title"
    className={cn('text-foreground-primary heading-lg', props.className)}
  />
);
export const CardDescription: FC<CardDescriptionProps> = (props) => (
  <div
    {...props}
    data-slot="card-description"
    className={cn('text-foreground-secondary caption', props.className)}
  />
);
